import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Mail, MessageCircle, MoreVertical, Send, SlidersHorizontal } from 'lucide-react';
import type { SessionResponse } from '@worki/shared';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { currentLocale } from '../i18n.js';
import { formatDateTime } from '../lib/format.js';
import { AnchoredMenu } from './AnchoredMenu.js';
import { Modal } from './Modal.js';
import { useToast } from './Toast.js';
import { Button, Field, Input, Spinner } from './ui.js';

interface QuoteSend {
  format?: string | null;
  status?: string | null;
  at: string;
  channel: string | null;
  to: string | null;
  actor: string | null;
  /** HIST 2.10 — a copy of the approved quote, which asked the customer for nothing. */
  copy?: boolean;
}

/**
 * QUO-021 — every time this quote left the building.
 *
 * Read from the audit trail rather than kept alongside it, so a row here cannot
 * exist without the audit entry that recorded the send. A quote that was sent
 * twice shows twice: "sent" is an event, and `sentAt` only remembers the first.
 */
export function SendHistoryDialog({
  open,
  quoteId,
  onClose,
  onResend,
  resendLabel,
}: {
  open: boolean;
  quoteId: string;
  onClose: () => void;
  /**
   * QUO-024 — the reason someone opens this list is usually that they are
   * about to add a line to it. Optional because a caller with no send dialog of
   * its own has nothing to offer here, and a menu item that opens nothing is
   * worse than no menu item.
   */
  onResend?: () => void;
  /** What that item says instead of "send again" — on an approved quote, a copy. */
  resendLabel?: string;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const menuAnchor = useRef<HTMLButtonElement>(null);

  const sends = useQuery({
    queryKey: ['quote-sends', quoteId],
    queryFn: () => api.get<{ items: QuoteSend[] }>(`/quotes/${quoteId}/sends`),
    enabled: open,
  });

  const items = sends.data?.items ?? [];

  return (
    <Modal
      open={open}
      title={t('quotes.sendHistory')}
      onClose={onClose}
      headerActions={
        <>
          <button
            type="button"
            ref={menuAnchor}
            aria-label={t('quotes.sendMenu')}
            onClick={() => setMenuOpen((was) => !was)}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
          >
            <MoreVertical className="h-[18px] w-[18px]" />
          </button>
          <AnchoredMenu open={menuOpen} onClose={() => setMenuOpen(false)} anchorRef={menuAnchor} align="end" width={200}>
            {onResend && (
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onClose();
                  onResend();
                }}
                className="w-full text-start px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors flex items-center gap-2"
              >
                <Send className="h-4 w-4 text-muted-foreground" />
                {resendLabel ?? t('quotes.sendAgain')}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setSettingsOpen((was) => !was);
              }}
              className="w-full text-start px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors flex items-center gap-2"
            >
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
              {t('quotes.sendSettings')}
            </button>
          </AnchoredMenu>
        </>
      }
    >
      {settingsOpen && <SendSettings onSaved={() => setSettingsOpen(false)} />}

      {sends.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('quotes.noSends')}</p>
      ) : (
        <ol className="space-y-2">
          {items.map((send) => {
            const Glyph = send.channel === 'whatsapp' ? MessageCircle : send.channel === 'email' ? Mail : Send;
            return (
              <li
                key={`${send.at}-${send.to ?? ''}`}
                className="flex items-start gap-3 rounded-xl bg-muted/50 px-3 py-2.5"
              >
                <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary grid place-items-center">
                  <Glyph className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="text-sm font-medium">
                    {send.channel ? t(`quotes.${send.channel === 'whatsapp' ? 'whatsapp' : 'email'}`) : t('quotes.sendQuote')}
                    {/* Marked, because a copy and a send look the same in a list of
                        channels and dates, and only one of them asked for an answer. */}
                    {send.format && <span className="ms-1 text-xs text-muted-foreground">{t(`history.format${send.format === 'both' ? 'Both' : send.format === 'pdf' ? 'Pdf' : 'Link'}`)}</span>}
                    {send.status && <span className="ms-1 text-xs text-muted-foreground">{t(`history.delivery.${send.status}`)}</span>}
                    {send.copy && (
                      <span className="ms-1.5 px-1.5 py-0.5 rounded-full bg-info/10 text-info text-[10px] font-medium align-middle">
                        {t('history.copyEntry')}
                      </span>
                    )}
                  </p>
                  {send.to && (
                    <p className="text-xs text-muted-foreground truncate" dir="ltr">
                      {t('quotes.sentTo', { to: send.to })}
                    </p>
                  )}
                  <p className="text-[11px] text-muted-foreground ltr-nums">
                    {formatDateTime(send.at, locale)}
                    {send.actor ? ` · ${send.actor}` : ''}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Modal>
  );
}

/**
 * QUO-024 — the one setting this list is about.
 *
 * A row here says a quote went out and when; what happens next if nobody
 * answers is the follow-up threshold, and until now it could only be changed on
 * the personal-area screen, several taps away from the question that raises it.
 * It is saved to the business, not to the quote, and says so.
 */
function SendSettings({ onSaved }: { onSaved: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { session, refresh } = useSession();
  const [hours, setHours] = useState(String(session?.business.quoteReminderHours ?? ''));

  const save = useMutation({
    mutationFn: () => api.patch<SessionResponse>('/auth/profile', { quoteReminderHours: Number(hours) }),
    onSuccess: () => {
      refresh();
      toast.success(t('quotes.sendSettingsSaved'));
      onSaved();
    },
  });

  const value = Number(hours);
  const valid = Number.isInteger(value) && value >= 1 && value <= 168;

  return (
    <section className="rounded-xl border border-border bg-muted/30 p-3 space-y-2">
      <Field label={t('profile.quoteReminderHours')} htmlFor="send-reminder-hours">
        <Input
          id="send-reminder-hours"
          type="number"
          min={1}
          max={168}
          dir="ltr"
          value={hours}
          invalid={!valid}
          onChange={(event) => setHours(event.target.value)}
        />
      </Field>
      <p className="text-[11px] text-muted-foreground">{t('profile.quoteReminderHoursHint')}</p>
      <div className="flex justify-end">
        <Button variant="primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
          {t('common.save')}
        </Button>
      </div>
    </section>
  );
}
